// Central bill-total computation, mirroring the Android CalculateBillFragment
// math (subtotal → discount → service charge → tax + CESS → parcel → round off).
// Shared by the Bill screen, Edit Bill and the restaurant Order Panel so every
// settlement path produces identical numbers and stores the same fields.

export interface BillLineInput {
  // Pre-tax line amount (qty × unit price), already net of any per-line discount.
  total: number;
  // GST rate for the line (percent).
  taxRatePercent: number;
  // CESS rate for the line (percent), optional.
  cessPercent?: number;
}

export interface BillTotalsInput {
  lines: BillLineInput[];
  // Bill-level discount: a flat amount plus a percent of the gross bill.
  discountAmount?: number;
  discountPercent?: number;
  // Service charge: a flat amount plus a percent of the post-discount amount.
  serviceChargeAmount?: number;
  serviceChargePercent?: number;
  // Parcel / delivery charge added after tax.
  parcelCharges?: number;
  // Free-form extra charges added after tax (Android Bill.extraCharges).
  extraCharges?: number;
  // Inclusive tax: the line price already contains GST + CESS (extract it);
  // otherwise GST + CESS are added on top.
  taxInclusive?: boolean;
  // When false (non-GST / estimate bill) no GST or CESS is applied.
  gstFlag?: boolean;
  // IGST regime → a single IGST figure; otherwise CGST + SGST (tax / 2 each).
  igst?: boolean;
  // Round the final total to the nearest rupee.
  roundOffEnabled?: boolean;
}

export interface BillTotals {
  billAmt: number;       // gross subtotal (Σ line totals)
  discount: number;      // resolved discount amount
  serviceCharge: number; // resolved service-charge amount
  tax: number;           // total GST
  cgst: number;
  sgst: number;
  igst: number;
  cess: number;          // total CESS
  parcel: number;
  extraCharges: number;
  roundOff: number;
  finalTotal: number;
}

export function computeBillTotals(input: BillTotalsInput): BillTotals {
  const {
    lines,
    discountAmount = 0,
    discountPercent = 0,
    serviceChargeAmount = 0,
    serviceChargePercent = 0,
    parcelCharges = 0,
    extraCharges = 0,
    taxInclusive = false,
    gstFlag = true,
    igst = false,
    roundOffEnabled = true,
  } = input;

  const billAmt = lines.reduce((s, l) => s + l.total, 0);
  const discount = discountAmount + (billAmt * discountPercent) / 100;
  const afterDisc = Math.max(0, billAmt - discount);
  const serviceCharge =
    serviceChargeAmount + (afterDisc * serviceChargePercent) / 100;

  let tax = 0;
  let cess = 0;
  if (gstFlag) {
    for (const l of lines) {
      // Distribute the bill discount proportionally across lines.
      const share = billAmt > 0 ? l.total / billAmt : 0;
      const taxable = Math.max(0, l.total - discount * share);
      const r = (l.taxRatePercent || 0) / 100;
      const c = (l.cessPercent || 0) / 100;
      if (taxInclusive) {
        // Price contains GST + CESS → back them out of the gross taxable.
        const net = taxable / (1 + r + c);
        tax += net * r;
        cess += net * c;
      } else {
        tax += taxable * r;
        cess += taxable * c;
      }
    }
  }

  const parcel = parcelCharges;
  // Inclusive tax is already inside afterDisc, so don't add it again.
  const raw = afterDisc + serviceCharge + parcel + extraCharges + (taxInclusive ? 0 : tax + cess);
  const finalTotal = roundOffEnabled ? Math.round(raw) : raw;
  const roundOff = finalTotal - raw;

  const cgst = igst ? 0 : tax / 2;
  const sgst = igst ? 0 : tax / 2;
  const igstAmt = igst ? tax : 0;

  return {
    billAmt,
    discount,
    serviceCharge,
    tax,
    cgst,
    sgst,
    igst: igstAmt,
    cess,
    parcel,
    extraCharges,
    roundOff,
    finalTotal,
  };
}
